import React, { useEffect, useState } from "react";
import { X, MapPin, Users, Trophy, Gamepad2, UserPlus, UserCheck, Loader2, ShieldCheck } from "lucide-react";
import api, { mediaUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { ReviewerBadge } from "@/components/games/gameMeta";
import ReportButton from "@/components/public/ReportButton";

function Avatar({ m, size = "w-20 h-20", text = "text-2xl" }) {
  return m.avatar ? (
    <img src={mediaUrl(m.avatar)} alt={m.nickname || m.name} className={`${size} rounded-2xl object-cover border border-white/10`} />
  ) : (
    <span className={`${size} rounded-2xl bg-[hsl(var(--primary))] grid place-items-center ${text} font-black text-[hsl(var(--primary-foreground))]`}>
      {(m.nickname || m.name || "?")[0].toUpperCase()}
    </span>
  );
}

export default function MemberModal({ userId, onClose, onFollowChange }) {
  const { user } = useAuth();
  const [m, setM] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pubGames, setPubGames] = useState([]);
  const [revRank, setRevRank] = useState(null);

  useEffect(() => {
    if (!userId) return;
    setM(null);
    api.get(`/members/${userId}`).then((r) => setM(r.data)).catch(() => { toast.error("Could not load profile"); onClose(); });
    api.get(`/gamehub/by/${userId}`).then((r) => { setPubGames(r.data.items); setRevRank(r.data.reviewer_rank); }).catch(() => setPubGames([]));
  }, [userId, onClose]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const toggleFollow = async () => {
    if (!user) return toast.error("Sign in to follow members");
    setBusy(true);
    try {
      const res = m.is_following
        ? await api.delete(`/members/${userId}/follow`)
        : await api.post(`/members/${userId}/follow`);
      setM({ ...m, is_following: res.data.following, follower_count: res.data.follower_count });
      onFollowChange?.(userId, res.data);
    } catch {
      toast.error("Action failed");
    } finally { setBusy(false); }
  };

  if (!userId) return null;
  const isSelf = user && user.user_id === userId;

  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="member-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full sm:max-w-lg glass rounded-t-3xl sm:rounded-3xl p-6 max-h-[90vh] overflow-y-auto no-scrollbar">
        <button onClick={onClose} data-testid="member-modal-close" className="absolute top-4 right-4 p-2 rounded-full bg-white/5 hover:bg-white/10 text-white/70"><X size={18} /></button>
        {!m ? (
          <div className="py-16 grid place-items-center text-white/40"><Loader2 className="animate-spin" /></div>
        ) : (
          <>
            <div className="flex items-start gap-4">
              <Avatar m={m} />
              <div className="min-w-0 flex-1 pt-1">
                <h3 className="text-xl font-bold text-white truncate" data-testid="member-nickname">{m.nickname || m.name || "Gamer"}</h3>
                {m.name && m.nickname && <p className="text-sm text-white/50 truncate">{m.name}</p>}
                <div className="mt-1.5 flex flex-wrap items-center gap-3 text-xs text-white/50">
                  {m.country && <span className="inline-flex items-center gap-1"><MapPin size={12} /> {m.country}</span>}
                  {m.team && <span className="inline-flex items-center gap-1"><Users size={12} /> {m.team}</span>}
                </div>
                {revRank && <div className="mt-2"><ReviewerBadge rank={revRank} /></div>}
                {m.verified_channels?.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5" data-testid="member-verified-channels">
                    {m.verified_channels.map((c) => (
                      <a key={c.provider} href={c.channel_url} target="_blank" rel="noopener noreferrer" data-testid={`member-channel-${c.provider}`}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] text-[11px] font-bold">
                        <ShieldCheck size={11} /> {c.provider === "nimotv" ? "NimoTV" : c.provider[0].toUpperCase() + c.provider.slice(1)}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="mt-5 flex items-center gap-6">
              <div><span className="text-xl font-bold font-head text-white" data-testid="member-followers">{m.follower_count}</span><span className="ml-1.5 text-xs text-white/40">Followers</span></div>
              <div><span className="text-xl font-bold font-head text-white">{m.following_count}</span><span className="ml-1.5 text-xs text-white/40">Following</span></div>
              {!isSelf && (
                <button onClick={toggleFollow} disabled={busy} data-testid="member-follow-btn"
                  className={`ml-auto inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-bold transition-colors disabled:opacity-60 ${m.is_following ? "bg-white/10 text-white hover:bg-white/15" : "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:brightness-110"}`}>
                  {busy ? <Loader2 size={15} className="animate-spin" /> : m.is_following ? <UserCheck size={15} /> : <UserPlus size={15} />}
                  {m.is_following ? "Following" : "Follow"}
                </button>
              )}
            </div>

            {m.games?.length > 0 && (
              <div className="mt-6">
                <div className="mono uppercase text-[10px] tracking-[0.2em] text-white/40 mb-2 flex items-center gap-1.5"><Gamepad2 size={12} /> Games</div>
                <div className="flex flex-wrap gap-2">
                  {m.games.map((g) => <span key={g} className="px-3 py-1 rounded-full bg-white/5 border border-white/10 text-sm text-white/80">{g}</span>)}
                </div>
              </div>
            )}

            {m.titles?.length > 0 && (
              <div className="mt-6">
                <div className="mono uppercase text-[10px] tracking-[0.2em] text-white/40 mb-2 flex items-center gap-1.5"><Trophy size={12} /> Championship Titles</div>
                <div className="space-y-2">
                  {m.titles.map((t, i) => (
                    <div key={i} className="flex items-center gap-2.5 text-sm text-white/85"><Trophy size={14} className="text-yellow-400 shrink-0" /> {t}</div>
                  ))}
                </div>
              </div>
            )}

            {pubGames.length > 0 && (
              <div className="mt-6" data-testid="member-published-games">
                <div className="mono uppercase text-[10px] tracking-[0.2em] text-white/40 mb-2 flex items-center gap-1.5"><Gamepad2 size={12} /> Published Games</div>
                <div className="grid grid-cols-2 gap-2">
                  {pubGames.map((g) => (
                    <Link key={g.id} to={`/games/${g.id}`} onClick={onClose} data-testid={`member-game-${g.id}`} className="flex items-center gap-2 p-2 rounded-lg bg-white/5 hover:bg-white/10">
                      <img src={mediaUrl(g.img)} alt="" className="w-12 h-9 rounded object-cover shrink-0" />
                      <div className="min-w-0"><div className="text-xs font-bold text-white truncate">{g.name}</div><div className="text-[11px] text-white/50">★ {g.rating_count ? g.rating_avg.toFixed(1) : "–"}/10</div></div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {!m.games?.length && !m.titles?.length && !pubGames.length && <p className="mt-6 text-sm text-white/40">This gamer hasn't added more details yet.</p>}
            {!isSelf && <div className="mt-6 pt-4 border-t border-white/10 flex justify-end"><ReportButton type="profile" id={userId} ownerId={userId} label="Report profile" className="text-xs text-white/40" /></div>}
          </>
        )}
      </div>
    </div>
  );
}
